import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import {
  Button,
  cn,
  ConfirmationDialog,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
  DropdownTrigger,
  RiArrowRightSLine,
  RiCheckLine,
} from 'component-library';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBulkPatchDraftPlot, useDeleteDraftPlotBulkDelete } from '@/api/rest/resources/plot';
import { CropsEnum, LandManagementProcessEnum, PlotOwnershipEnum } from '@/api/rest/resources/types/plot';

import { PLOT_LAND_TYPES } from '../constants';
import { BulkEditLabelDialog } from './BulkEditLabelDialog';

type BulkEditDropdownProps = {
  selectedIds: string[];
  organizationId: string;
  onSuccess: () => void;
  onAddToGroup: () => void;
};

const SUB_CONTENT_STYLES = cn(
  'z-dialog min-w-[160px] rounded-md bg-white-100 py-2 elevation-1',
  'animate-in fade-in-0 slide-in-from-left-1',
);

const SUB_TRIGGER_CLASS = cn(
  'typography-body1 flex cursor-pointer items-center justify-between gap-2 px-4 py-1.5 text-text-primary outline-none',
  'data-[highlighted]:bg-neutral-black-4',
);

export const BulkEditDropdown = ({ selectedIds, organizationId, onSuccess, onAddToGroup }: BulkEditDropdownProps) => {
  const { t } = useTranslation();
  // TODO(MVP-10075): convert to the standardized submit/isSubmitting hook pattern (see
  // useDraftPlotGroupActions.ts) for consistent toasts, query invalidation, and suspense support.
  const { mutateAsync: bulkPatch, isPending: isPatching } = useBulkPatchDraftPlot();
  const { mutateAsync: bulkDelete, isPending: isDeleting } = useDeleteDraftPlotBulkDelete();
  const isLoading = isPatching || isDeleting;
  const [open, setOpen] = useState(false);
  const [pendingCrops, setPendingCrops] = useState<CropsEnum[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLabelDialogOpen, setIsLabelDialogOpen] = useState(false);

  const patch = async (field: Record<string, unknown>) => {
    await bulkPatch({
      bodyData: { ids: selectedIds, ...field },
    });
    setOpen(false);
    onSuccess();
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) setPendingCrops([]);
  };

  return (
    <>
      <Dropdown open={open} onOpenChange={handleOpenChange}>
        <DropdownTrigger asChild>
          <Button variant='outline' loading={isLoading}>
            {t('shared.uploadPlots.review.bulkActions')}
          </Button>
        </DropdownTrigger>
        <DropdownContent align='end' side='top' sideOffset={4}>
          <DropdownItem
            onSelect={() => {
              setOpen(false);
              onAddToGroup();
            }}
          >
            {t('shared.uploadPlots.review.addToGroup')}
          </DropdownItem>

          <DropdownSeparator />

          {/* Edit land type */}
          <DropdownMenuPrimitive.Sub>
            <DropdownMenuPrimitive.SubTrigger className={SUB_TRIGGER_CLASS}>
              {t('shared.uploadPlots.bulkEdit.editLandType')}
              <RiArrowRightSLine size={16} className='text-text-secondary' />
            </DropdownMenuPrimitive.SubTrigger>
            <DropdownMenuPrimitive.Portal>
              <DropdownMenuPrimitive.SubContent className={SUB_CONTENT_STYLES} sideOffset={2}>
                {PLOT_LAND_TYPES.map((pt) => (
                  <DropdownItem key={pt} onSelect={() => patch({ land_type: pt })}>
                    {t(`global.plotTypes.${pt}`)}
                  </DropdownItem>
                ))}
              </DropdownMenuPrimitive.SubContent>
            </DropdownMenuPrimitive.Portal>
          </DropdownMenuPrimitive.Sub>

          {/* Edit crop type */}
          <DropdownMenuPrimitive.Sub>
            <DropdownMenuPrimitive.SubTrigger className={SUB_TRIGGER_CLASS}>
              {t('shared.uploadPlots.bulkEdit.editCropType')}
              <RiArrowRightSLine size={16} className='text-text-secondary' />
            </DropdownMenuPrimitive.SubTrigger>
            <DropdownMenuPrimitive.Portal>
              <DropdownMenuPrimitive.SubContent className={cn(SUB_CONTENT_STYLES, 'flex flex-col py-0')} sideOffset={2}>
                <div className='max-h-[280px] overflow-y-auto py-2'>
                  {Object.values(CropsEnum).map((c) => (
                    <DropdownMenuPrimitive.CheckboxItem
                      key={c}
                      className={cn(SUB_TRIGGER_CLASS, 'justify-start gap-2')}
                      checked={pendingCrops.includes(c)}
                      onCheckedChange={(checked) =>
                        setPendingCrops((prev) => (checked ? [...prev, c] : prev.filter((x) => x !== c)))
                      }
                      onSelect={(event) => event.preventDefault()}
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                          pendingCrops.includes(c)
                            ? 'border-primary-100 bg-primary-100 text-white-100'
                            : 'border-neutral-black-23',
                        )}
                      >
                        {pendingCrops.includes(c) && <RiCheckLine size={12} />}
                      </span>
                      {t(`global.crops.${c}`)}
                    </DropdownMenuPrimitive.CheckboxItem>
                  ))}
                </div>
                <div className='border-t border-neutral-black-12 py-2'>
                  <DropdownItem
                    disabled={pendingCrops.length === 0}
                    onSelect={() => {
                      patch({ crops: pendingCrops });
                      setPendingCrops([]);
                    }}
                  >
                    {`${t('global.ui.buttons.apply')} (${pendingCrops.length})`}
                  </DropdownItem>
                </div>
              </DropdownMenuPrimitive.SubContent>
            </DropdownMenuPrimitive.Portal>
          </DropdownMenuPrimitive.Sub>

          {/* Edit land management */}
          <DropdownMenuPrimitive.Sub>
            <DropdownMenuPrimitive.SubTrigger className={SUB_TRIGGER_CLASS}>
              {t('shared.uploadPlots.editPlot.fields.landManagement')}
              <RiArrowRightSLine size={16} className='text-text-secondary' />
            </DropdownMenuPrimitive.SubTrigger>
            <DropdownMenuPrimitive.Portal>
              <DropdownMenuPrimitive.SubContent className={SUB_CONTENT_STYLES} sideOffset={2}>
                {Object.values(LandManagementProcessEnum).map((lm) => (
                  <DropdownItem key={lm} onSelect={() => patch({ land_management_process: lm })}>
                    {t(`shared.uploadPlots.landManagement.${lm}`)}
                  </DropdownItem>
                ))}
              </DropdownMenuPrimitive.SubContent>
            </DropdownMenuPrimitive.Portal>
          </DropdownMenuPrimitive.Sub>

          {/* Edit ownership */}
          <DropdownMenuPrimitive.Sub>
            <DropdownMenuPrimitive.SubTrigger className={SUB_TRIGGER_CLASS}>
              {t('shared.uploadPlots.bulkEdit.editOwnership')}
              <RiArrowRightSLine size={16} className='text-text-secondary' />
            </DropdownMenuPrimitive.SubTrigger>
            <DropdownMenuPrimitive.Portal>
              <DropdownMenuPrimitive.SubContent className={SUB_CONTENT_STYLES} sideOffset={2}>
                {Object.values(PlotOwnershipEnum).map((o) => (
                  <DropdownItem key={o} onSelect={() => patch({ ownership: o })}>
                    {t(`shared.uploadPlots.ownership.${o}`)}
                  </DropdownItem>
                ))}
              </DropdownMenuPrimitive.SubContent>
            </DropdownMenuPrimitive.Portal>
          </DropdownMenuPrimitive.Sub>

          <DropdownSeparator />

          <DropdownItem
            onSelect={() => {
              setOpen(false);
              setIsLabelDialogOpen(true);
            }}
          >
            {t('shared.uploadPlots.bulkEdit.editLabel')}
          </DropdownItem>

          <DropdownSeparator />

          {/* Delete selected */}
          <DropdownItem
            className='text-error'
            onSelect={() => {
              setOpen(false);
              setIsDeleteDialogOpen(true);
            }}
          >
            {t('shared.uploadPlots.review.deleteSelected')}
          </DropdownItem>
        </DropdownContent>
      </Dropdown>

      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        header={t('shared.uploadPlots.review.deleteSelectedConfirm.header', { count: selectedIds.length })}
        body={t('shared.uploadPlots.review.deleteSelectedConfirm.body', { count: selectedIds.length })}
        CancelAction={
          <Button variant='outline' onClick={() => setIsDeleteDialogOpen(false)}>
            {t('global.ui.buttons.cancel')}
          </Button>
        }
        PrimaryAction={
          <Button
            className='bg-error hover:bg-error-dark'
            loading={isDeleting}
            onClick={async () => {
              await bulkDelete({ queryParams: { ids: selectedIds.join(',') } });
              setIsDeleteDialogOpen(false);
              onSuccess();
            }}
          >
            {t('shared.uploadPlots.review.deleteSelected')}
          </Button>
        }
      />

      {/* Local boundary so this dialog's suspense queries don't bubble up to the page-level loader on first mount. */}
      <Suspense fallback={null}>
        <BulkEditLabelDialog
          open={isLabelDialogOpen}
          organizationId={organizationId}
          plotIds={selectedIds}
          onClose={() => setIsLabelDialogOpen(false)}
          onSaved={() => {
            setIsLabelDialogOpen(false);
            onSuccess();
          }}
        />
      </Suspense>
    </>
  );
};
